import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { appPaths } from "../../../shared/routing/appPaths";
import { useWorkspace } from "../../../shell/workspace";
import AppButton from "../../../ui/buttons/AppButton";
import { ONTOLOGY_SLICE_PARAMETER, slicePath, type SliceLens } from "./sliceLens";
import { processTerm } from "../../../api/content/processTerms";
import { classText } from "../../workflows/inputCards/typeWords";

interface SliceBarProps {
  readonly slice: SliceLens;
  /** The commit shown. */
  readonly commit: string;
}

/**
 * Above the terms while the Data model shows a Thread's ontology slice (F95): where the slice
 * comes from, the model as it is or as the run's change set proposes it (PC-7), the
 * whole model beside it in the split view, and the change set's review, where its Turtle is
 * edited. The terms below show only the slice; each term's card has its Turtle.
 */
export default function SliceBar({ slice, commit }: SliceBarProps) {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const wholeQuery = new URLSearchParams(query);
  wholeQuery.delete(ONTOLOGY_SLICE_PARAMETER);
  const whole = wholeQuery.size === 0 ? appPaths.ontology({ version: commit }) : `${appPaths.ontology({ version: commit })}?${wholeQuery.toString()}`;
  const proposed = slice.proposed !== null && commit === slice.proposed.head;
  return (
    <div className="app-surface-muted flex flex-wrap items-center gap-x-3 gap-y-1 rounded px-3 py-2 text-sm">
      <span>
        {t("ontologyViewer.slice.of", { count: slice.iris.size })}{" "}
        <Link to={appPaths.thread(slice.threadId, slice.entry)} className="app-link underline">{slice.title}</Link>
      </span>
      {slice.proposed === null || slice.baseCommit === null ? null : (
        <span className="app-page-switcher app-flow-display-tabs flex" role="group" aria-label={classText(processTerm("ContentVersion"), t)}>
          {[
            { key: "now", commit: slice.baseCommit, pressed: !proposed },
            { key: "proposed", commit: slice.proposed.head, pressed: proposed },
          ].map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={option.pressed}
              className="app-page-switcher-button px-3 py-1 text-xs"
              onClick={() => { void navigate(slicePath(option.commit, slice)); }}
            >
              {t(`ontologyViewer.slice.${option.key}`)}
            </button>
          ))}
        </span>
      )}
      <span className="ml-auto flex flex-wrap items-center gap-2">
        {slice.proposed === null ? null : (
          <Link to={appPaths.review(slice.proposed.changeSetId)} className="app-link text-xs underline">{t("ontologyViewer.slice.editInReview")}</Link>
        )}
        <AppButton variant="secondary" size="xs" onClick={() => { workspace.open(whole, { placement: "beside" }); }}>
          {t("ontologyViewer.slice.wholeBeside")}
        </AppButton>
        <Link to={whole} className="app-link text-xs underline">{t("ontologyViewer.slice.close")}</Link>
      </span>
    </div>
  );
}
